import { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';

/** `YYYY-MM-DDTHH:mm`（秒付き可）→ 表示用 `YYYY/MM/DD HH:mm` */
export function formatIsoDateTimeForDisplay(isoDateTime: string): string {
  if (!isoDateTime) return '';
  const m = isoDateTime.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (m) return `${m[1]}/${m[2]}/${m[3]} ${m[4]}:${m[5]}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoDateTime)) {
    return `${isoDateTime.replace(/-/g, '/')} 00:00`;
  }
  return isoDateTime.replace('T', ' ').replace(/-/g, '/').slice(0, 16);
}

/** datetime-local 用に `YYYY-MM-DDTHH:mm` へ正規化 */
export function toDateTimeLocalValue(isoDateTime: string): string {
  if (!isoDateTime) return '';
  const m = isoDateTime.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : isoDateTime.slice(0, 16);
}

/**
 * 表示文字列（yyyy/MM/dd HH:mm 等）→ datetime-local 値。
 * 不正なら null。
 */
export function parseDisplayDateTimeToLocal(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const m = trimmed.match(
    /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::\d{2})?)?$/
  );
  if (!m) return null;
  const y = m[1];
  const mo = m[2].padStart(2, '0');
  const d = m[3].padStart(2, '0');
  const hh = (m[4] ?? '0').padStart(2, '0');
  const mm = (m[5] ?? '0').padStart(2, '0');
  const next = `${y}-${mo}-${d}T${hh}:${mm}`;
  const probe = new Date(`${y}-${mo}-${d}T${hh}:${mm}:00`);
  if (Number.isNaN(probe.getTime())) return null;
  if (
    probe.getFullYear() !== Number(y) ||
    probe.getMonth() + 1 !== Number(mo) ||
    probe.getDate() !== Number(d)
  ) {
    return null;
  }
  return next;
}

type BaseDateTimeFieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (yyyyMmDdThhmm: string) => void;
  min?: string;
  max?: string;
  lang?: string;
  title?: string;
  className?: string;
  /** 外側に Form.Label があるとき */
  hideLabel?: boolean;
  required?: boolean;
  isInvalid?: boolean;
  name?: string;
  size?: 'sm';
  autoComplete?: string;
};

/**
 * 表示は yyyy/MM/dd HH:mm（手入力可）。
 * カレンダーボタン／フォーカス時に datetime-local ピッカーも開く。
 * Modal 内でも手入力で任意日時を入れられる（ネイティブピッカー失敗時の逃げ道）。
 */
export default function BaseDateTimeField({
  id,
  label,
  value,
  onChange,
  min,
  max,
  lang,
  title,
  className,
  hideLabel = false,
  required = false,
  isInvalid = false,
  name,
  size,
  autoComplete = 'off',
}: BaseDateTimeFieldProps) {
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const displayId = `${id}-display`;
  const localValue = toDateTimeLocalValue(value);
  const [draft, setDraft] = useState(() => formatIsoDateTimeForDisplay(localValue));

  useEffect(() => {
    setDraft(formatIsoDateTimeForDisplay(toDateTimeLocalValue(value)));
  }, [value]);

  const clampAndEmit = (next: string) => {
    let out = toDateTimeLocalValue(next);
    if (!out) return;
    if (max && out > toDateTimeLocalValue(max)) out = toDateTimeLocalValue(max);
    if (min && out < toDateTimeLocalValue(min)) out = toDateTimeLocalValue(min);
    onChange(out);
  };

  const commitDraft = () => {
    const parsed = parseDisplayDateTimeToLocal(draft);
    if (!parsed) {
      setDraft(formatIsoDateTimeForDisplay(localValue));
      return;
    }
    clampAndEmit(parsed);
  };

  const openPicker = () => {
    const el = hiddenInputRef.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === 'function') {
        void el.showPicker();
        return;
      }
    } catch {
      // fall through
    }
    el.focus();
    el.click();
  };

  return (
    <div className={`sample-datetime-field ${className ?? ''}`.trim()}>
      {hideLabel ? null : (
        <Form.Label htmlFor={displayId} className="fw-semibold" title={title ?? label}>
          {label}
          {required ? (
            <>
              {' '}
              <span className="text-danger">*</span>
            </>
          ) : null}
        </Form.Label>
      )}
      <div className="sample-datetime-field__controls">
        <Form.Control
          id={displayId}
          type="text"
          inputMode="numeric"
          size={size}
          value={draft}
          placeholder="yyyy/MM/dd HH:mm"
          title={title ?? `${label} ${draft}`.trim()}
          aria-label={`${label}${draft ? ` ${draft}` : ''}`}
          aria-required={required || undefined}
          isInvalid={isInvalid}
          autoComplete={autoComplete}
          className="sample-datetime-field__display"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitDraft();
            }
          }}
        />
        <button
          type="button"
          className="sample-datetime-field__picker-btn"
          aria-label={title ?? label}
          title={title ?? label}
          onClick={openPicker}
        >
          <i className="fas fa-calendar-alt" aria-hidden />
        </button>
        <Form.Control
          ref={hiddenInputRef}
          id={id}
          name={name}
          type="datetime-local"
          value={localValue}
          min={min ? toDateTimeLocalValue(min) : undefined}
          max={max ? toDateTimeLocalValue(max) : undefined}
          lang={lang}
          required={required}
          autoComplete={autoComplete}
          tabIndex={-1}
          className="sample-datetime-field__native"
          onChange={(e) => {
            const selected = e.target.value;
            if (!selected) return;
            clampAndEmit(selected);
          }}
        />
      </div>
    </div>
  );
}
